import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { getInquiryQueryOptions, useInquiry } from '@/stores/leads.store'
import { useNotes } from '@/stores/notes.store'
import { useAttachments } from '@/stores/attachments.store'
import { useTasksList } from '@/stores/tasks.store'
import { useContactQuoteInteractionsByQuoteIds, useQuoteLitesByIds } from '@/stores/quotes.store'
import { useInquiryOverlaps } from '@/stores/overlaps.store'
import { ParentTypeEnum, InquiryStageEnum, InquiryRejectReasonEnum } from '@/gen/cortado/v1/settings_pb'
import {
  ContactHeader,
  ActivityTabs,
  LeadInquiryInfo,
  LeadQuickActions,
  LeadQuotesCard,
  LeadEventsCard,
  InquiryStageDropdown,
  InquiryAssigneeDropdown,
  LeadOverlapBanner,
  LeadArchiveMenu,
} from '@/components/leads'
import { Badge } from '@/components/ui/badge'
import { MoveToEventsButton } from '@/components/leads/id/move-to-events-button'
import { GenerateEventsDialog } from '@/components/leads/id/generate-events-dialog'
import { RejectReasonDialog } from '@/components/leads/id/reject-reason-dialog'
import { LeadDetailErrorState } from '@/components/leads/id/lead-detail-error-state'
import { LeadLastContact } from '@/components/leads/lead-mail-badge'
import { formatEnumName, isBooked } from '@/lib/enum-utils'
import { isAcceptedWithoutEvent } from '@/lib/leads'
import { isValidUUID } from '@/lib/uuid'
import type { Inquiry } from '@/gen/cortado/v1/inquiries_pb'
import { FileText, ShieldX, MessageSquareText, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import { canPreloadOrganizationQueries, preloadQueries } from '@/lib/route-preloading'
import { LeadReferralStatus } from '@/components/leads/referrals/lead-referral-status'

export const Route = createFileRoute('/_app/_home/leads/$id')({
  component: LeadDetailPage,
  validateSearch: z.object({
    from: z.string().optional(),
    tab: z.enum(['conversations']).optional(),
    // A thread to open on the Conversations tab, e.g. from a reply notification.
    thread: z.string().optional(),
  }),
  loader: ({ context, params }) => {
    if (!isValidUUID(params.id) || !canPreloadOrganizationQueries(context.auth)) return
    preloadQueries(context.queryClient, [
      getInquiryQueryOptions(params.id),
    ])
  },
})

function RejectReasonBanner({ reason, notes }: { reason: number; notes?: string }) {
  const label = formatEnumName(reason, InquiryRejectReasonEnum)

  return (
    <div className="rounded-lg border border-status-danger-border bg-status-danger-bg/60 px-4 py-3">
      <div className="flex items-start gap-3">
        <ShieldX className="mt-0.5 h-4 w-4 shrink-0 text-status-danger-fg" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-status-danger-fg">
            Rejected — {label}
          </p>
          {notes && (
            <div className="mt-1.5 flex items-start gap-1.5">
              <MessageSquareText className="mt-0.5 h-3 w-3 shrink-0 text-status-danger-fg/80" />
              <p className="text-xs text-status-danger-fg/80">{notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * High-prominence, good-news banner for a lead that has converted to a
 * booking. Distinct from ConvertedRecordBanner's mid-page "FYI, this data is
 * stale" grammar (still used by quote→invoice conversion) and from the
 * "no event yet" warning below it — those are yellow and low-weight; this is
 * green, larger, and carries its own action, styled after
 * DocumentStatusBanner's top-of-page status pattern.
 */
function BookedLeadBanner({ inquiry }: { inquiry: Inquiry }) {
  const eventId = inquiry.eventIds?.[0]

  return (
    <div className="rounded-lg border border-status-success-border bg-status-success-bg p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-status-success-fg" />
          <div className="min-w-0">
            <p className="font-medium text-status-success-fg">This lead is booked</p>
            <p className="mt-1 text-sm text-status-success-fg/85">
              The customer accepted a quote and this lead converted to a booked event. Changes made here won't update the event.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="purple">Booked</Badge>
          {eventId && (
            <Button asChild size="sm" variant="outline">
              <Link to="/events/$id" params={{ id: eventId }}>
                View Event
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function LeadDetailPage() {
  const { id } = Route.useParams()

  if (!isValidUUID(id)) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="flex-1 border border-dashed py-12">
          <EmptyMedia variant="icon">
            <FileText className="h-6 w-6" />
          </EmptyMedia>
          <EmptyDescription>We couldn't load this lead right now.</EmptyDescription>
        </Empty>
      </div>
    )
  }

  return <LeadDetailContent id={id} />
}

function LeadDetailContent({ id }: { id: string }) {
  const { from, tab, thread } = Route.useSearch()
  const navigate = useNavigate()
  const [showBookingDialog, setShowBookingDialog] = useState(false)
  const [showGenerateEventsDialog, setShowGenerateEventsDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [quoteCreateRequestId, setQuoteCreateRequestId] = useState(0)
  const quotesCardRef = useRef<HTMLDivElement>(null)

  // Memoize breadcrumbs to prevent infinite loops
  const customBreadcrumbs = useMemo(() => {
    if (from === 'calendar') {
      return [
        { label: 'Home', href: '/home' },
        { label: 'Calendar', href: '/calendar' },
        { label: 'Lead' }
      ]
    }
    return undefined
  }, [from])

  // Set breadcrumbs based on navigation source
  useBreadcrumbFromRoute(customBreadcrumbs)

  const { inquiry, isLoading, isError, error } = useInquiry(id)

  // Fetch notes, tasks, and quotes separately using the stores
  const { notes } = useNotes({
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: id,
  })

  const { tasks } = useTasksList({
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: id,
  })

  const { attachments } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: id,
  })

  // Use the inquiry's quote IDs to fetch only relevant quotes
  const { quotes } = useQuoteLitesByIds(inquiry?.quoteIds || [])
  const {
    interactions: leadQuoteInteractions,
    totalCount: leadQuoteInteractionCount,
    isLoading: quoteInteractionsLoading,
  } = useContactQuoteInteractionsByQuoteIds({
    contactId: inquiry?.contact?.id,
    quoteIds: inquiry?.quoteIds || [],
    limitPerQuote: 100,
  })

  // Fetch overlapping leads and events
  const { overlappingInquiries, overlappingEvents, hasOverlaps } = useInquiryOverlaps(id)

  const handleCreateQuote = () => {
    setQuoteCreateRequestId((current) => current + 1)
    window.requestAnimationFrame(() => {
      quotesCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        {/* Header Skeleton */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-6 w-6 rounded" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-6 w-6 rounded" />
            </div>
          </div>
        </div>

        {/* Content Grid Skeleton */}
        <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Inquiry Information Card Skeleton */}
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-56 mt-2" />
              </CardHeader>
              <CardContent className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-4 w-4" />
                    <div className="space-y-1 flex-1">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions Skeleton */}
            <div className="grid gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* Quotes Card Skeleton */}
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-4 w-48 mt-2" />
              </CardHeader>
              <CardContent>
                <div className="border border-dashed rounded-lg p-12">
                  <Skeleton className="h-5 w-5 mx-auto mb-2" />
                  <Skeleton className="h-4 w-32 mx-auto" />
                </div>
              </CardContent>
            </Card>

            {/* Activity Tabs Card Skeleton */}
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-4 w-56 mt-2" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Skeleton className="h-10 w-24" />
                    <Skeleton className="h-10 w-24" />
                    <Skeleton className="h-10 w-24" />
                  </div>
                  <div className="border border-dashed rounded-lg p-12">
                    <Skeleton className="h-5 w-5 mx-auto mb-2" />
                    <Skeleton className="h-4 w-32 mx-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (isError || !inquiry) {
    return <LeadDetailErrorState error={error} />
  }

  const contact = inquiry.contact

  return (
    <div className="flex min-h-full flex-col gap-4 p-4 pb-8">
      <ContactHeader
        firstName={contact?.firstName}
        lastName={contact?.lastName}
        email={contact?.email}
        phoneNumber={contact?.phoneNumber}
        inquiryId={inquiry.id}
        currentContactId={contact?.id}
        contact={contact}
        isArchived={!!contact?.archivedAt}
        actionDropdown={
          <div className="flex items-center gap-2">
            {inquiry.archivedAt && <Badge variant="secondary">Archived</Badge>}
            <MoveToEventsButton
              inquiry={inquiry}
              onRequestMoveToEvents={() => setShowGenerateEventsDialog(true)}
            />
            <InquiryAssigneeDropdown
              inquiryId={inquiry.id}
              assignedTo={inquiry.assignedTo}
              assignedToUser={inquiry.assignedToUser}
            />
            <InquiryStageDropdown
              inquiryId={inquiry.id}
              currentStage={inquiry.stage}
              onBookRequested={() => {
                // Booking accepts the lead; a booked lead is already Accepted.
                if (!isBooked(inquiry)) {
                  setShowGenerateEventsDialog(true)
                }
              }}
              onStageChangedToRejected={() => setShowRejectDialog(true)}
            />
            <LeadArchiveMenu inquiry={inquiry} />
          </div>
        }
      />
      <LeadLastContact
        tags={inquiry.tags}
        contactFirstName={contact?.firstName}
        lastInboundAt={inquiry.lastInboundAt}
        lastOutboundAt={inquiry.lastOutboundAt}
        lastMessageDirection={inquiry.lastMessageDirection}
      />

      {inquiry.stage === InquiryStageEnum.INQUIRY_STAGE_REJECTED && inquiry.rejectReason != null && inquiry.rejectReason !== InquiryRejectReasonEnum.INQUIRY_REJECT_REASON_UNKNOWN && (
        <RejectReasonBanner reason={inquiry.rejectReason} notes={inquiry.rejectNotes} />
      )}

      {isBooked(inquiry) && <BookedLeadBanner inquiry={inquiry} />}

      <LeadReferralStatus organizationId={inquiry.organizationId} inquiryId={inquiry.id} />

      {isAcceptedWithoutEvent({ stage: inquiry.stage, bookingId: inquiry.bookingId }) && (
        <div className="rounded-lg border border-status-warning-border bg-status-warning-bg/60 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning-fg" />
            <p className="text-sm text-status-warning-fg">
              No event created for this accepted lead.{' '}
              <button
                type="button"
                onClick={() => setShowGenerateEventsDialog(true)}
                className="font-medium underline underline-offset-2 hover:opacity-80"
              >
                Generate Events →
              </button>
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
        <div className="space-y-4">
          <LeadInquiryInfo inquiry={inquiry} />
          <LeadQuickActions inquiry={inquiry} onCreateQuote={handleCreateQuote} />
        </div>

        <div className="space-y-4">
          {hasOverlaps && (
            <LeadOverlapBanner
              overlappingInquiries={overlappingInquiries}
              overlappingEvents={overlappingEvents}
            />
          )}

          <LeadEventsCard
            inquiryId={inquiry.id}
            events={inquiry.events}
            inquiryEventType={inquiry.eventTypeKey}
            inquiryEventLocation={inquiry.eventLocation}
            overlappingInquiries={overlappingInquiries}
            overlappingEvents={overlappingEvents}
            quotes={quotes}
          />

          <div ref={quotesCardRef}>
            <LeadQuotesCard
              inquiry={inquiry}
              quotes={quotes}
              createRequestId={quoteCreateRequestId}
            />
          </div>

          <ActivityTabs
            organizationId={inquiry.organizationId}
            parentType={ParentTypeEnum.PARENT_TYPE_INQUIRY}
            parentId={inquiry.id}
            notes={notes}
            tasks={tasks}
            attachments={attachments}
            quoteInteractions={leadQuoteInteractions}
            quoteInteractionCount={leadQuoteInteractionCount}
            quoteInteractionsLoading={quoteInteractionsLoading}
            showQuoteInteractions
            customerEmail={contact?.email}
            emailContactId={contact?.id}
            showWorkflowRuns
            workflowSourceResourceId={inquiry.id}
            workflowTriggerEntityType="INQUIRY"
            initialTab={tab}
            initialThreadId={thread}
          />
        </div>
      </div>

      {/* Dialog shown when stage is changed to ACCEPTED */}
      <GenerateEventsDialog
        open={showGenerateEventsDialog}
        onOpenChange={setShowGenerateEventsDialog}
        inquiryId={inquiry.id}
        onEventsGenerated={() => setShowBookingDialog(true)}  // eventId ignored - uses inquiry.eventIds after refresh
      />

      {/* Dialog shown when stage is changed to REJECTED */}
      <RejectReasonDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        inquiryId={inquiry.id}
        onRejected={() => {
          setShowRejectDialog(false)
          toast.success('Lead rejected')
        }}
      />

      {/* Dialog shown when lead has been converted to a booking */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lead Accepted</DialogTitle>
            <DialogDescription>
              This lead has been accepted and converted to a booking. You can view the booking details or stay on this page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingDialog(false)}>
              Stay on Lead
            </Button>
            <Button
              onClick={() =>
                navigate({
                  to: '/events/$id',
                  params: { id: inquiry.eventIds[0] },
                })
              }
              disabled={!inquiry.eventIds?.[0]}
            >
              View Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
